// The link index of the open folder: notes, sketches, migrations, and the entities of every context's
// current model and of every sketch. Loading it reads each context's current model once.
import { linkableEntities, linkableTypes, type IndexedModel, type LinkIndex } from '@modelviewer/core';
import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { loadModel, refKey, sketchRef, useWorkspace } from '../state/workspace';

export function useLinkIndex(): LinkIndex {
  const discovery = useWorkspace((s) => s.discovery);
  const sketches = useWorkspace((s) => s.sketches);
  const notes = useWorkspace((s) => s.notes);
  const currentKeys = useMemo(() => (discovery?.sets ?? []).map((set) => refKey({ setKey: set.key, migrationId: null })), [discovery]);
  const current = useWorkspace(useShallow((s) => currentKeys.map((k) => s.models[k])));

  useEffect(() => {
    for (const set of discovery?.sets ?? []) void loadModel({ setKey: set.key, migrationId: null });
  }, [discovery]);

  return useMemo<LinkIndex>(() => {
    const models: IndexedModel[] = [];
    (discovery?.sets ?? []).forEach((set, i) => {
      const loaded = current[i];
      if (loaded) models.push({ ref: { setKey: set.key, migrationId: null }, name: set.contextName, kind: 'context', entities: linkableEntities(loaded.result.doc.model), types: linkableTypes(loaded.result.doc.model) });
    });
    for (const s of Object.values(sketches)) models.push({ ref: sketchRef(s), name: s.doc.title, kind: 'sketch', entities: linkableEntities(s.doc.model), types: linkableTypes(s.doc.model) });
    return {
      notes: Object.values(notes).map((n) => ({ id: n.id, title: n.title })),
      sketches: Object.values(sketches).map((s) => ({ id: s.id, title: s.doc.title, setKey: s.setKey })),
      models,
      migrations: (discovery?.sets ?? []).flatMap((set) => set.migrations.map((m) => ({ setKey: set.key, context: set.contextName, id: m.id, name: m.name }))),
    };
  }, [discovery, current, sketches, notes]);
}
